import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Check } from "@phosphor-icons/react";
import { useId, useMemo, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type ColorSwatchValue = string | string[];

export type ColorSwatchGroupProps = Omit<RadioGroupPrimitive.RadioGroupProps, "defaultValue" | "disabled" | "onValueChange" | "value"> & {
  value: ColorSwatchValue;
  onValueChange: (value: ColorSwatchValue) => void;
  options: ColorSwatchValue[];
  label?: ReactNode;
  disabled?: boolean;
  getOptionLabel?: (option: ColorSwatchValue, index: number) => string;
  optionClassName?: string;
};

const serializeColorOption = (option: ColorSwatchValue) => JSON.stringify(option);

const defaultOptionLabel = (option: ColorSwatchValue, index: number) => {
  if (Array.isArray(option)) {
    return `Palette ${index + 1}: ${option.join(", ")}`;
  }

  return option;
};

function SwatchPreview({ option }: { option: ColorSwatchValue }) {
  const colors = Array.isArray(option) ? option : [option];

  return (
    <span className="bfrs-flex bfrs-size-full bfrs-overflow-hidden bfrs-rounded-[inherit]">
      {colors.map((color, index) => (
        <span key={`${color}-${index}`} className="bfrs-min-w-0 bfrs-flex-1" style={{ backgroundColor: color }} />
      ))}
    </span>
  );
}

export function ColorSwatchGroup({
  className,
  optionClassName,
  value,
  onValueChange,
  options,
  label,
  disabled,
  getOptionLabel,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  ...props
}: ColorSwatchGroupProps) {
  const generatedId = useId();
  const labelId = label ? `${generatedId}-label` : undefined;
  const optionMap = useMemo(() => new Map(options.map((option) => [serializeColorOption(option), option])), [options]);
  const serializedValue = serializeColorOption(value);

  return (
    <div className={cn("bfrs-grid bfrs-gap-2", className)}>
      {label && (
        <span id={labelId} className={cn("bfrs-text-[12px] bfrs-font-semibold bfrs-text-text-primary", disabled && "bfrs-text-text-disabled")}>
          {label}
        </span>
      )}
      <RadioGroupPrimitive.Root
        {...props}
        value={serializedValue}
        aria-label={ariaLabel ?? (label ? undefined : "Color options")}
        aria-labelledby={ariaLabelledBy ?? labelId}
        aria-disabled={disabled}
        className="bfrs-flex bfrs-flex-wrap bfrs-gap-2"
        onValueChange={(nextValue) => {
          const option = optionMap.get(nextValue);
          if (option) {
            onValueChange(option);
          }
        }}
      >
        {options.map((option, index) => {
          const optionValue = serializeColorOption(option);
          const optionLabel = getOptionLabel?.(option, index) ?? defaultOptionLabel(option, index);

          return (
            <RadioGroupPrimitive.Item
              key={optionValue}
              value={optionValue}
              disabled={disabled}
              aria-label={optionLabel}
              title={optionLabel}
              className={cn(
                "bfrs-relative bfrs-flex bfrs-h-9 bfrs-w-12 bfrs-items-center bfrs-justify-center bfrs-rounded-[var(--bfrs-radius-control)] bfrs-border-2 bfrs-border-border-control bfrs-bg-surface-card bfrs-p-1 bfrs-shadow-low bfrs-outline-none bfrs-transition hover:bfrs-border-primary/70 focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus disabled:bfrs-cursor-not-allowed disabled:bfrs-opacity-50 data-[state=checked]:bfrs-border-primary data-[state=checked]:bfrs-shadow-elevated",
                optionClassName
              )}
            >
              <SwatchPreview option={option} />
              <RadioGroupPrimitive.Indicator className="bfrs-absolute -bfrs-right-1 -bfrs-top-1 bfrs-flex bfrs-size-4 bfrs-items-center bfrs-justify-center bfrs-rounded-pill bfrs-bg-primary bfrs-text-primary-contrast bfrs-shadow-low">
                <Check size={10} weight="bold" />
              </RadioGroupPrimitive.Indicator>
            </RadioGroupPrimitive.Item>
          );
        })}
      </RadioGroupPrimitive.Root>
    </div>
  );
}

ColorSwatchGroup.displayName = "ColorSwatchGroup";
